import { v4 as uuidv4 } from 'uuid';
import { saveCampaignSession } from './campaignTracking';

// Initialize window._mtm (data layer) immediately, preserving window._mtm if it already exists
window._mtm = window._mtm || [];

/* This method will be executed as soon as the global window.MatomoTagManager object is available
 but before any container has been set up.
*/
window.matomoTagManagerAsyncInit = function (MatomoTagManager) {
    MatomoTagManager.THROW_ERRORS = false;
    /* wait to push onto _mtm until the Matomo Tag Manager script has loaded. It still doesn't
    work to push multiple calls of the same type (identify, page, track) into the _mtm array at
    page load time because by the time Mataomo Tag Manager starts processing them only the last
    value for things like eventName and eventProperties will be used. You will see duplicate calls
    if you try to push two track calls. This is the same behavior with gtm.js and Google Tag Manager.
    */
    window._mtm.push(...window.mtmPreLoadDataLayer);
};

export const pusher = payload => {
    if (window.mtmLoaded) {
        window._mtm.push(payload);
    } else {
        window.mtmPreLoadDataLayer.push(payload);
    }
};

export const analytics = {
    identify: (userId, userProperties) => {
        pusher({ userProperties: undefined });
        pusher({ event: 'identify', userId, userProperties, hitId: uuidv4() });
    },
    page: (pageCategory, pageName, pageProperties) => {
        pusher({ pageProperties: undefined });
        pusher({ event: 'page', pageCategory, pageName, pageProperties, hitId: uuidv4() });
    },
    track: (eventName, eventProperties) => {
        pusher({ eventProperties: undefined });
        pusher({ event: 'track', eventName, eventProperties, hitId: uuidv4() });
    },
};

export function pushVisitorToMTM(visitor) {
    if (visitor && (visitor.ip || visitor.country || visitor.isBot !== null || visitor.isInternal !== null)) {
        window._mtm.push({
            event: 'visitor',
            visitorIpAddress: visitor.ip ?? '',
            visitorCountry: visitor.country ?? '',
            visitorIsBot: visitor.isBot ?? false,
            visitorIsInternal: visitor.isInternal ?? false,
        });
    }
}

/**
 * Load the external Matomo Tag Manager javascript configured for our sites
 * @param {string} containerId Matomo Tag Manager container id
 * @example UFefytkf_dev_7adf05bd8a6ab9900b8297d9
 */
export function loadMTMModule(containerId) {
    let _mtm = window._mtm;
    _mtm.push({ 'mtm.startTime': new Date().getTime(), event: 'mtm.Start' });
    (function () {
        let d = document,
            g = d.createElement('script'),
            s = d.getElementsByTagName('script')[0];
        g.async = true;
        g.src = `https://metrics.siemens-cdn.com/js/container_${containerId}.js`;
        s.parentNode.insertBefore(g, s);
    })();
}

export function appendChildToBody(element) {
    if (document.body) {
        document.body.appendChild(element);
    } else {
        const bodyLoadedObserver = new MutationObserver(() => {
            if (document.body) {
                document.body.appendChild(element);
                bodyLoadedObserver.disconnect();
            }
        });
        bodyLoadedObserver.observe(document.documentElement, { childList: true });
    }
}

export function loadCookieBanner(version, settingsId) {
    const script = document.createElement('script');
    script.id = 'usercentrics-cmp';
    script.setAttribute('data-version', version);
    script.setAttribute('data-settings-id', settingsId);
    script.src = `https://data.cdn.siemens.com/?settingsId=${settingsId}`;
    script.async = true;
    appendChildToBody(script);
}

export const mtmLoader = (account, config, callback) => {
    if (account && account.sid) {
        if (!window.mtmLoaded) {
            // Create campaign session only on first mtmLoader call
            saveCampaignSession({ sessionId: account.sid, userId: account.user?.contact_id });
            window._mtm.push({ sessionId: account.sid });
        }
        // allow userId to be pushed after Matomo Tag Manager load because anonymous users can be assigned
        // a contact_id over the course of a session
        if (account.user && account.user.contact_id) {
            window._mtm.push({ userId: account.user.contact_id });
        }

        if (!window.mtmLoaded) {
            window._mtm.push({ initialPageHitId: uuidv4() });
            callback(config.containerId);
            window.mtmLoaded = true;
        }
    }
};

/**
 * Initialize Matomo Tag Manager
 * @param {object} config config.mtm from disw.init
 * @param {string} config.containerId Matomo container id
 * @param {string | undefined} config.pageId The contentful page entry ID
 * @param {function} callback optional callback used for initializing Matomo Tag Manager. a default callback is used
 */
export async function initializeMTM(config = {}, callback = loadMTMModule) {
    /* Initialize config variable and apply default values */
    // eslint-disable-next-line no-prototype-builtins
    if (!config.hasOwnProperty('containerId')) {
        throw new Error('config.containerId is required');
    }
    config.cookieBannerEnabled = config.cookieBannerEnabled ?? true;
    config.cookieBannerSettingsId = config.cookieBannerSettingsId || 'GXj83XOdm';
    config.cookieBannerVersion = config.cookieBannerVersion || 'latest';

    /* Initialize variable to prevent Matomo Tag Manager from being loaded each time an account event is emitted.
     * sid does not change so we don't want to push it on the dataLayer each time an event is emitted.
     * contact_id can change in the case where an anonymous user is assigned a contact_id in the session.
     */
    window.mtmLoaded = false;

    // Put page, identify, and track calls here until session and contact_id are pushed and Matomo is loaded
    window.mtmPreLoadDataLayer = [];

    // Initialize a lightweight analytics object that manages segment methods
    window.analytics = analytics;

    // Push DSA header values to _mtm immediately because they are needed to determine if tags will trigger
    // eslint-disable-next-line no-undef
    disw.account.subscribeToVisitor(pushVisitorToMTM);

    // If we have a pageId, push it to the data layer
    if (typeof config.pageId !== 'undefined') {
        window._mtm.push({ pageId: config.pageId });
        window.analytics.pageId = config.pageId;
    }

    // Load consent framework (UserCentrics)
    if (config.cookieBannerEnabled) {
        loadCookieBanner(config.cookieBannerVersion, config.cookieBannerSettingsId);
    }

    // Listen for disw.account to retrieve the session
    // Only after we get a sessionId can we load Matomo Tag Manager
    // eslint-disable-next-line no-undef
    disw.account.subscribeToAccount(account => mtmLoader(account, config, callback));
}
